/**
 * ShareDialog - Dialog for sharing a conversation
 *
 * Shows the share URL and allows copying to clipboard
 */

import { useState } from 'react';
import { Check, Copy, Link } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface ShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shareUrl: string | null;
  expiresAt: string | null;
  loading?: boolean;
  error?: string | null;
}

export function ShareDialog({
  open,
  onOpenChange,
  shareUrl,
  expiresAt,
  loading = false,
  error = null,
}: ShareDialogProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (shareUrl) {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatExpiryDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link className="h-5 w-5" />
            Share Conversation
          </DialogTitle>
          <DialogDescription>
            Anyone with this link can view a read-only copy of this conversation.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : error ? (
            <div className="text-center py-4">
              <p className="text-sm text-red-500">{error}</p>
            </div>
          ) : shareUrl ? (
            <>
              <div className="space-y-2">
                <div
                  className="p-3 bg-muted rounded-md text-sm break-all select-all cursor-text font-mono"
                  onClick={(e) => {
                    // Select all text when clicked
                    const selection = window.getSelection();
                    const range = document.createRange();
                    range.selectNodeContents(e.currentTarget);
                    selection?.removeAllRanges();
                    selection?.addRange(range);
                  }}
                >
                  {shareUrl}
                </div>
                <Button
                  variant="outline"
                  onClick={handleCopy}
                  className="w-full"
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4 mr-2 text-green-500" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-2" />
                      Copy link
                    </>
                  )}
                </Button>
              </div>
              {expiresAt && (
                <p className="text-xs text-muted-foreground text-center">
                  This link expires on {formatExpiryDate(expiresAt)}
                </p>
              )}
            </>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ShareDialog;
